// Grades capture metadata into a traffic-light (good / ok / low) per metric, and defines the manual
// "before you upload" reminder items. Thresholds are capture-type aware — 360 cameras top out around
// 30 fps at high resolution, so grading them against a 60 fps bar would wrongly flag every 360 capture.
// All thresholds live here; tune freely as the team learns what actually predicts a good reconstruction.

export const LEVEL = { GOOD: 'good', OK: 'ok', LOW: 'low', INFO: 'info' };

// height (px) tiers for 2D; long-edge (px) tiers for the raw 360 sensor frame.
const RES = {
  '2d': { good: 2160, ok: 1080 },      // 4K / 1080p by frame height
  '360': { good: 5000, ok: 3840 },     // stitched equirect long edge (e.g. 5.7K / 4K)
};
const FPS = {
  '2d': { good: 50, ok: 28 },          // 60 good · 30 ok · 24 low
  '360': { good: 28, ok: 23 },         // 30 good · 24 ok · <24 low (360 cams rarely exceed 30 @ high res)
};
// Bits per pixel per frame — resolution-independent proxy for compression quality.
const BPP = { good: 0.08, ok: 0.04 };
const DURATION = { good: 45, ok: 30 };  // seconds; <30s red, 30–45s amber, ≥45s green

const tier = (value, t) => (value >= t.good ? LEVEL.GOOD : value >= t.ok ? LEVEL.OK : LEVEL.LOW);
const fmt = (n, d = 0) => (n == null || !isFinite(n) ? '—' : Number(n).toFixed(d));

function codecLabel(codec) {
  if (!codec) return null;
  const c = codec.toLowerCase();
  if (c.startsWith('hvc') || c.startsWith('hev')) return 'H.265 / HEVC';
  if (c.startsWith('avc')) return 'H.264';
  return codec;
}

/**
 * Build the auto-check rows for a capture. `cameraType` is '2d' or '360'.
 * Returns [{ id, label, level, value, hint }]. Metrics we couldn't read are omitted (not failed).
 */
export function gradeCapture(meta, cameraType) {
  const type = cameraType === '360' ? '360' : '2d';

  if (!meta || !meta.ok) {
    return [{
      id: 'metadata',
      label: 'Capture metadata',
      level: LEVEL.INFO,
      value: 'Not readable',
      hint: "Couldn't read this file's details in the browser — it'll still upload and is validated during processing.",
    }];
  }

  const rows = [];
  const longEdge = Math.max(meta.width || 0, meta.height || 0);

  // Resolution
  if (meta.width && meta.height) {
    const metric = type === '360' ? longEdge : (meta.height || 0);
    rows.push({
      id: 'resolution',
      label: 'Resolution',
      level: tier(metric, RES[type]),
      value: `${meta.width}×${meta.height}`,
      hint: type === '360'
        ? 'Stitched 360 (equirect) resolution — both lenses combined. Higher captures more surface detail and features.'
        : 'Higher resolution captures more detail and more trackable features.',
    });
  }

  // Frame rate
  if (meta.fps) {
    rows.push({
      id: 'fps',
      label: 'Frame rate',
      level: tier(meta.fps, FPS[type]),
      value: `${fmt(meta.fps)} fps`,
      hint: type === '360'
        ? '360 cameras usually cap near 30 fps at high resolution — 30 is ideal, below 24 risks motion blur.'
        : 'Higher fps gives more sharp frames to choose from and less motion blur on a moving capture.',
    });
  } else if (meta.ok) {
    rows.push({
      id: 'fps',
      label: 'Frame rate',
      level: LEVEL.INFO,
      value: '—',
      hint: 'Frame rate was not readable for this file.',
    });
  }

  // Bitrate (as bits-per-pixel-per-frame when we have the pieces, else raw Mbps info)
  if (meta.bitrateMbps && meta.width && meta.height && meta.fps) {
    const bpp = (meta.bitrateMbps * 1e6) / (meta.width * meta.height * meta.fps);
    rows.push({
      id: 'bitrate',
      label: 'Bitrate',
      level: tier(bpp, BPP),
      value: `${fmt(meta.bitrateMbps, 1)} Mbps`,
      hint: 'Low bitrate for the resolution means heavy compression, which smears the fine detail features rely on.',
    });
  } else if (meta.bitrateMbps) {
    rows.push({
      id: 'bitrate',
      label: 'Bitrate',
      level: LEVEL.INFO,
      value: `${fmt(meta.bitrateMbps, 1)} Mbps`,
      hint: 'Overall data rate of the capture.',
    });
  }

  // Duration
  if (meta.durationSec) {
    rows.push({
      id: 'duration',
      label: 'Duration',
      level: tier(meta.durationSec, DURATION),
      value: formatDuration(meta.durationSec),
      hint: 'A very short clip may not cover the whole asset — longer, thorough passes reconstruct better.',
    });
  }

  // Codec (informational)
  const codec = codecLabel(meta.codec);
  if (codec) {
    rows.push({ id: 'codec', label: 'Format', level: LEVEL.INFO, value: codec, hint: 'Capture codec — informational.' });
  }

  return rows;
}

function formatDuration(sec) {
  const s = Math.round(sec);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}m ${r}s` : `${r}s`;
}

// Capture-quality reminders the file can't reveal — shown as informational FYI cards (not a checklist
// to confirm). Grouped so they lay out as a couple of cards rather than one long list.
export const MANUAL_GROUPS = [
  {
    title: 'Coverage & movement',
    items: [
      { label: 'Full coverage', hint: 'The whole asset was captured — all sides and angles, no gaps.' },
      { label: 'Overlap & steady pace', hint: 'Slow, steady movement with plenty of overlap — the biggest factor in a clean reconstruction.' },
      { label: 'Stable capture', hint: 'Steady height (walkthrough) or stable flight lines (drone); minimal moving objects in frame.' },
    ],
  },
  {
    title: 'Conditions & setup',
    items: [
      { label: 'Even lighting', hint: 'Avoid deep shadow and blown-out highlights.' },
      { label: 'Difficult surfaces', hint: 'Reflective, glass, or featureless areas reconstruct poorly — worth noting.' },
      { label: 'Clean lens', hint: 'No smudges, water, or fingerprints on the lens.' },
      { label: 'Targets (if used)', hint: 'Coded targets placed and visible if this capture relies on them for scale/alignment.' },
    ],
  },
];
